import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, Switch, message, Space, Popconfirm, Upload, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import api from '../../utils/api';

const { Text } = Typography;

interface Store {
  id: number;
  store_code: string | null;
  store_name: string;
  region_id: number;
  google_location_name: string;
  current_rating?: string | null;
  is_active: boolean;
  region_name?: string;
}

interface Region {
  id: number;
  name: string;
  parent_id: number | null;
}

const AdminStoresPage: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [selectedRegionId, setSelectedRegionId] = useState<number | undefined>(undefined);
  const [pageSize, setPageSize] = useState(20);
  const [form] = Form.useForm();
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  const fetchRegions = useCallback(async () => {
    try {
      const response = await api.get('/admin/regions/flat');
      setRegions(response.data);
    } catch (error: any) {
      message.error('Failed to fetch regions: ' + (error.response?.data?.detail || error.message));
    }
  }, []);

  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (selectedRegionId) {
        params.region_id = selectedRegionId;
      }
      const response = await api.get('/admin/stores', { params });
      setStores(response.data);
    } catch (error: any) {
      message.error('Failed to fetch stores: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  }, [selectedRegionId]);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const handleCreate = () => {
    setEditingStore(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalVisible(true);
  };

  const handleImport = () => {
    setImportModalVisible(true);
    setImportResult(null);
  };

  const handleImportSubmit = async (file: File) => {
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/admin/stores/import-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setImportResult(response.data);
      
      if (response.data.error_count === 0 && response.data.skipped_count === 0) {
        message.success(`Successfully imported ${response.data.imported_count} stores`);
      } else {
        message.warning(
          `Import completed: ${response.data.imported_count} imported, ` +
          `${response.data.skipped_count} skipped, ${response.data.error_count} errors`
        );
      }
      
      // Refresh store list
      fetchStores();
      
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message || 'Import failed';
      message.error('Import failed: ' + errorMsg);
      setImportResult({
        error_count: 1,
        errors: [{ error: errorMsg }],
      });
    } finally {
      setImporting(false);
    }
  };

  const uploadProps = {
    accept: '.csv',
    beforeUpload: (file: File) => {
      handleImportSubmit(file);
      return false; // Prevent auto upload
    },
    showUploadList: false,
  };

  const handleEdit = (store: Store) => {
    setEditingStore(store);
    form.setFieldsValue(store);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/admin/stores/${id}`);
      message.success('Store deleted successfully');
      fetchStores();
    } catch (error: any) {
      message.error('Failed to delete store: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingStore) {
        await api.patch(`/admin/stores/${editingStore.id}`, values);
        message.success('Store updated successfully');
      } else {
        await api.post('/admin/stores', values);
        message.success('Store created successfully');
      }
      setModalVisible(false);
      fetchStores();
    } catch (error: any) {
      if (error.response?.status === 422) {
        // Validation error
        const errors = error.response.data.detail;
        if (Array.isArray(errors)) {
          errors.forEach((err: any) => {
            form.setFields([
              {
                name: err.loc[err.loc.length - 1],
                errors: [err.msg],
              },
            ]);
          });
        }
      } else {
        const errDetail = error.response?.data?.detail || error.response?.data?.error || error.message;
        message.error((editingStore ? 'Update' : 'Create') + ' failed: ' + errDetail);
      }
    }
  };

  const columns = [
    {
      title: 'Store Code',
      dataIndex: 'store_code',
      key: 'store_code',
    },
    {
      title: 'Store Name',
      dataIndex: 'store_name',
      key: 'store_name',
    },
    {
      title: 'Region',
      dataIndex: 'region_name',
      key: 'region_name',
    },
    {
      title: 'Google Location Name',
      dataIndex: 'google_location_name',
      key: 'google_location_name',
    },
    {
      title: 'Current Rating',
      dataIndex: 'current_rating',
      key: 'current_rating',
      render: (rating: string | null) => rating ? `${rating}⭐` : '-',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (isActive ? 'Active' : 'Inactive'),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_: any, record: Store) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this store?"
            description="The store and all its reviews will be permanently removed. This cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Store Management</h1>
        <Space>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />} loading={importing}>
              Import CSV
            </Button>
          </Upload>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            New Store
          </Button>
        </Space>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Select
          placeholder="Filter by region (includes sub-regions)"
          allowClear
          style={{ width: 300 }}
          onChange={(value) => setSelectedRegionId(value)}
          value={selectedRegionId}
        >
          {regions.map((region) => (
            <Select.Option key={region.id} value={region.id}>
              {region.name}
            </Select.Option>
          ))}
        </Select>
      </div>

      <Table
        columns={columns}
        dataSource={stores}
        rowKey="id"
        loading={loading}
        pagination={{
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                onChange: (_page, size) => setPageSize(size),
              }}
      />

      <Modal
        title={editingStore ? 'Edit Store' : 'New Store'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Confirm"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="store_code"
            label="Store Code"
          >
            <Input placeholder="Optional, store code" />
          </Form.Item>
          <Form.Item
            name="store_name"
            label="Store Name"
            rules={[{ required: true, message: 'Please enter store name' }]}
          >
            <Input placeholder="Enter store name" />
          </Form.Item>
          <Form.Item
            name="region_id"
            label="Region"
            rules={[{ required: true, message: 'Please select a region' }]}
          >
            <Select placeholder="Select region">
              {regions.filter(r => r.parent_id === null || r.parent_id !== null).map((region) => (
                <Select.Option key={region.id} value={region.id}>
                  {region.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="google_location_name"
            label="Google Location Name"
            rules={[{ required: true, message: 'Please enter Google Location Name' }]}
          >
            <Input placeholder="Enter Google Location Name (must be unique)" />
          </Form.Item>
          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Import Stores from CSV"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setImportResult(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setImportModalVisible(false);
            setImportResult(null);
          }}>
            Close
          </Button>,
        ]}
        width={800}
      >
        <div>
          <div style={{ marginBottom: '16px' }}>
            <Text strong>CSV Format:</Text>
            <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <Text code>store_code, store_name, region, google_location_name</Text>
            </div>
            <div style={{ marginTop: '12px' }}>
              <Text type="secondary">
                • <Text strong>store_code</Text>: Optional, store code<br />
                • <Text strong>store_name</Text>: Required, store name<br />
                • <Text strong>region</Text>: Required, region name (must match existing region)<br />
                • <Text strong>google_location_name</Text>: Required, Google location name (must be unique)
              </Text>
            </div>
          </div>

          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />} loading={importing} type="primary" block>
              {importing ? 'Importing...' : 'Select CSV File'}
            </Button>
          </Upload>

          {importResult && (
            <div style={{ marginTop: '24px' }}>
              <Text strong>Import Results:</Text>
              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div>✅ Imported: <Text strong>{importResult.imported_count}</Text></div>
                <div>⏭️ Skipped: <Text strong>{importResult.skipped_count}</Text></div>
                <div>❌ Errors: <Text strong>{importResult.error_count}</Text></div>
              </div>

              {importResult.errors && importResult.errors.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <Text strong type="danger">Errors:</Text>
                  <div style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                    {importResult.errors.map((err: any, idx: number) => (
                      <div key={idx} style={{ marginBottom: '4px', fontSize: '12px' }}>
                        Row {err.row}: {err.store_name} - {err.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importResult.skipped && importResult.skipped.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <Text strong>Skipped (already exist):</Text>
                  <div style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                    {importResult.skipped.map((skip: any, idx: number) => (
                      <div key={idx} style={{ marginBottom: '4px', fontSize: '12px' }}>
                        Row {skip.row}: {skip.store_name} - {skip.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importResult.imported && importResult.imported.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <Text strong type="success">Successfully Imported:</Text>
                  <div style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                    {importResult.imported.map((imp: any, idx: number) => (
                      <div key={idx} style={{ marginBottom: '4px', fontSize: '12px' }}>
                        Row {imp.row}: {imp.store_name} (ID: {imp.store_id})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AdminStoresPage;
